import { useIntl } from 'react-intl'

import { cn } from '@gto/ui/cn'
import { CheckCircleIcon } from '@gto/ui/icons/solid'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import { type RedemptionOption } from '@/api/gen'
import { SubscriptionKindEnum, SubscriptionLevelEnum } from '@/pages/rewards/constants'

import { GtoSubscriptionCardHeader } from './gto-subscription-card-header'
import { RedeemButton } from './redeem-button'

export const GtoSubscriptionPlanCard = (option: RedemptionOption) => {
  return (
    <Paper
      as="article"
      className="flex-1 rounded-md border border-divider px-8 py-6 transition-[border-color,background] duration-200 ease-in-out hover:border-primary-main hover:bg-paper-elevation0 sm:px-6 sm:py-4"
    >
      <div className="flex flex-col gap-10 sm:gap-6">
        <GtoSubscriptionCardHeader {...option} />
        <RedeemButton
          isActive={option.is_active}
          plan={option}
        />
        <CardDescription
          level={option.subscription_level}
          type={option.subscription_type}
        />
      </div>
    </Paper>
  )
}

type CardDescriptionProps = { level: SubscriptionLevelEnum; type: number }

const CardDescription = ({ level, type }: CardDescriptionProps) => {
  const { messages } = useIntl()
  const shouldHighlightFirstItem = level !== SubscriptionLevelEnum.Starter

  const cashDescriptionMap = {
    [SubscriptionLevelEnum.Starter]: messages[
      'edge.plansSection.starterPlan.description'
    ] as unknown,
    [SubscriptionLevelEnum.Premium]: messages[
      'edge.plansSection.premiumPlan.description'
    ] as unknown,
    [SubscriptionLevelEnum.Elite]: messages['edge.plansSection.elitePlan.description'] as unknown,
    [SubscriptionLevelEnum.Ultra]: messages['edge.plansSection.ultraPlan.description'] as unknown,
  }

  const tournamentDescriptionMap = {
    [SubscriptionLevelEnum.Starter]: messages[
      'edge.plansSection.tournamentStarterPlan.description'
    ] as unknown,
    [SubscriptionLevelEnum.Premium]: messages[
      'edge.plansSection.tournamentPremiumPlan.description'
    ] as unknown,
    [SubscriptionLevelEnum.Elite]: messages[
      'edge.plansSection.tournamentElitePlan.description'
    ] as unknown,
    [SubscriptionLevelEnum.Ultra]: messages[
      'edge.plansSection.tournamentUltraPlan.description'
    ] as unknown,
  }

  const descriptionMap =
    type === (SubscriptionKindEnum.Tournament as number)
      ? tournamentDescriptionMap
      : cashDescriptionMap

  const items = Array.isArray(descriptionMap[level]) ? (descriptionMap[level] as string[]) : []

  return (
    <ul className="relative m-0 flex list-none flex-col gap-0.5 p-1">
      {items.map((item: string, index: number) => {
        if (!item.length) {
          return null
        }

        const isSoon = item.endsWith('||soon')
        const cleanText = isSoon ? item.replace('||soon', '') : item

        return (
          <li
            key={item}
            className="relative mb-2 flex w-full items-center justify-start p-0 text-left sm:mb-0"
          >
            <div className="flex items-center gap-2">
              <span className="text-primary-main">
                <CheckCircleIcon
                  height={18}
                  width={18}
                />
              </span>{' '}
              <div className={cn('relative min-w-0 flex-1', isSoon && 'pr-[36px]')}>
                <Typography
                  gutterBottom
                  className={cn(index === 0 && shouldHighlightFirstItem && 'font-semibold')}
                  variant="body2"
                  color={
                    index === 0 && shouldHighlightFirstItem ? 'text.primary' : 'text.secondary'
                  }
                >
                  {cleanText}
                </Typography>
                {isSoon && (
                  <span className="absolute right-0 top-1/2 inline-flex -translate-y-1/2 items-center rounded-[10px] border border-solid border-warning-main p-1 text-[0.625rem] font-semibold normal-case leading-[1.2] text-warning-main">
                    Soon
                  </span>
                )}
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
